import { Component, OnInit } from '@angular/core';
import {
	UntypedFormControl,
	UntypedFormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MfaMethod } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import { ApiService } from 'src/app/services/api.services';
import { AuthService } from 'src/app/services/auth.service';
import { LoginContainerComponent } from '../login-container/login-container.component';
import {
	MatButtonToggleGroup,
	MatButtonToggle,
} from '@angular/material/button-toggle';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { AutoFocusDirective } from '../../../directives/auto-focus.directive';
import { PhoneNumberPipe } from '../../../components/pipes';

export enum DisplayStep {
	selectMethod = 'selectMethod',
	verifyToken = 'verifyToken',
}
@Component({
	selector: 'app-login-mfa',
	templateUrl: './login-mfa.component.html',
	styleUrls: [
		'./login-mfa.component.scss',
		'../login.component.scss',
		'../../common/main.scss',
	],
	imports: [
		LoginContainerComponent,
		MatButtonToggleGroup,
		MatButtonToggle,
		MatButton,
		FormsModule,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		AutoFocusDirective,
		MatError,
		PhoneNumberPipe,
	],
})
export class LoginMfaComponent implements OnInit {
	userProfile?: UserProfile;

	emailChecked = false;
	mobileChecked = false;
	hideSms = false;

	displayStep: DisplayStep = DisplayStep.selectMethod;

	enteredToken = '';

	tokenValidated = false;

	formGroup = new UntypedFormGroup({
		enteredTokenField: new UntypedFormControl('', {
			validators: [],
			updateOn: 'change',
		}),
	});

	constructor(
		private authService: AuthService,
		private apiService: ApiService,
		private router: Router,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.apiService.getUserProfile().then((result) => {
			this.userProfile = result;
			if (!this.userProfile?.user?.phoneNumber?.rawPhoneNumber) {
				this.emailChecked = true;
				this.hideSms = true;
			}
		});
	}

	selectEmailButton(): void {
		this.emailChecked = true;
		this.mobileChecked = false;
	}

	selectMobileButton(): void {
		this.emailChecked = false;
		this.mobileChecked = true;
	}

	getSelectedMfaType(): MfaMethod | undefined {
		if (this.emailChecked) {
			return 'email';
		} else if (this.mobileChecked) {
			return 'sms';
		}
		return undefined;
	}

	backToLoginClicked(): void {
		this.router.navigateByUrl('/login');
	}

	backToSendTokenClicked(): void {
		this.displayStep = DisplayStep.selectMethod;
		this.enteredToken = '';
	}

	async goToVerifyClicked(): Promise<void> {
		const type = this.getSelectedMfaType();

		await this.requestToken();
		this.displayStep = DisplayStep.verifyToken;
	}

	async enteredTokenChanged(newValue: string): Promise<void> {
		this.enteredToken = newValue;
		this.formGroup.controls.enteredTokenField.setErrors(
			this.tokenFieldValidator()
		);
		if (newValue.length === 6 && newValue.match(/^\d{6}$/)) {
			await this.validateToken();
		}
	}

	async requestToken(): Promise<void> {
		const type = this.getSelectedMfaType();
		if (!type) {
			console.error('Should never see this: selectedMfaType is undefined');
			this.backToSendTokenClicked();
			return;
		}
		await this.apiService.requestMfaToken(type);
	}

	tokenFieldValidator(): any {
		const field = this.formGroup.controls.enteredTokenField;
		const containsOnlyValidChars = !!field.value.match(/^\d*$/);
		if (!containsOnlyValidChars) {
			return { invalidToken: true };
		}
		return null;
	}

	async validateToken(): Promise<void> {
		this.formGroup.controls.enteredTokenField.disable();

		const response = await this.authService.verifyMfaToken(this.enteredToken);

		if (response.mfaRequired) {
			// Token is invalid
			this.formGroup.controls.enteredTokenField.enable();
			this.formGroup.controls.enteredTokenField.setErrors({
				tokenValidationFailed: true,
			});
		} else {
			this.tokenValidated = true;
			this.router.navigateByUrl('/');
		}
	}
}
